import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { IEditInfo, IReceipt } from '@shared/models';
import { LoggerCodes, LoggerService } from '@shared/modules/logger/logger';
import {
  DeleteTransactionState,
  SetTransactionId,
  SetTransactionOAReviewPageEditMode,
  SetTransactionReviewInvestmentObjectivesMode,
  SetTransactionReviewPageEditMode,
} from '@store/actions/adapted-state/adapted-state.actions';
import { DeleteDrafts } from '@store/actions/draft/draft.actions';
import * as fromRootStore from '@store/index';
import {
  ActiveDraftChanged,
  CheckFirstNewAccountTransaction,
  setIsEditingAccount,
  setIsEditingEmployment,
  setIsEditingProfile,
} from '@store/index';
import { StartNewAccountCollection } from '@store/internal-transfers/actions/multi-internal-transfer.actions';
import { selectTransactionById } from '@store/selectors/transaction-state/transaction-state.selectors';
import { take } from 'rxjs';

import { ReceiptModalComponent } from './components/receipt-modal/receipt-modal.component';
import { ReceiptSectionComponent } from './components/receipt-section/receipt-section.component';

@Component({
  selector: 'app-receipt',
  templateUrl: './receipt.component.html',
  styleUrls: ['./receipt.component.scss'],
  standalone: true,
  imports: [ReceiptSectionComponent, ReceiptModalComponent, CommonModule],
})
export class ReceiptComponent implements OnInit {
  @Input()
  public set receiptArray(array: IReceipt[]) {
    this._receiptArray = array.filter((x) => x != null);
  }
  @Input() isMultiInheritanceReviewEditing: boolean = false;
  @Input() containerUrl: string = '';

  @Output()
  noRoutingEditing: EventEmitter<IReceipt | IEditInfo> = new EventEmitter<IReceipt | IEditInfo>();
  @Input() isDivorceTransfer: boolean;

  _receiptArray: IReceipt[] = [];

  private readonly personalInfoUrl: string = 'secure/multi-inheritance/personal-info';
  private readonly multiInheritanceNewAccountReviewUrl: string =
    '/secure/multi-inheritance/new-account-review';
  private readonly employmentInfoUrl: string = 'secure/multi-inheritance/employment';
  private readonly accountTriageUrl: string = 'secure/multi-inheritance/account-triage';

  // ITM OA NARC routes
  private readonly ITNewAccountReviewUrl: string = '/secure/internal-transfer/new-account-review';
  private readonly ITPersonalInfoUrl: string = 'secure/internal-transfer/personal-info';
  private readonly ITInvestmentObjUrl: string = 'secure/internal-transfer/investment-objectives';

  constructor(
    protected globalStore: Store<fromRootStore.State>,
    protected loggerService: LoggerService,
  ) {}

  ngOnInit() {
    this.globalStore.dispatch(
      SetTransactionReviewPageEditMode({ isReviewPageReachedAndEditMode: false }),
    );

    this.globalStore.dispatch(
      SetTransactionOAReviewPageEditMode({ isOAReviewPageReachedAndEditMode: false }),
    );

    this.globalStore.dispatch(
      SetTransactionReviewInvestmentObjectivesMode({
        isMultiReviewPageReachedAndSkipInvestmentRevision: false,
      }),
    );
  }

  userEdit(editInfo: IEditInfo) {
    const { editable, editRoute, editTransferId } = editInfo;
    const isNewAccountReviewEditing =
      this.multiInheritanceNewAccountReviewUrl === this.containerUrl;
    const isItmNarcEditing = this.ITNewAccountReviewUrl === this.containerUrl;

    if (editable && editRoute) {
      // Handle ITM NARC editing (account subflow or profile subflow)
      if (isItmNarcEditing) {
        this.loggerService.info(
          {
            eventId: 'itm-narc-page-edit',
            message: `ITM NARC Page Edit: ${editRoute.split('/').pop()}`,
          },
          LoggerCodes.GENERAL_SUCCESS,
        );

        if (editRoute === this.ITInvestmentObjUrl && editTransferId) {
          // Account Edit: set currentlyCollecting to the selected account
          this.globalStore.dispatch(StartNewAccountCollection({ transactionId: editTransferId }));
        } else if (editRoute === this.ITPersonalInfoUrl) {
          // Profile Edit: set isEditingProfile flag
          this.globalStore.dispatch(setIsEditingProfile({ isEditing: true }));
        }

        this.globalStore.dispatch(fromRootStore.Go({ path: [editRoute] }));
        return;
      }

      if (this.isMultiInheritanceReviewEditing || isNewAccountReviewEditing) {
        const eventId: string = this.isMultiInheritanceReviewEditing
          ? 'multi-inheritance-review-page-edit'
          : 'new-account-review-page-edit';
        const message: string = this.isMultiInheritanceReviewEditing
          ? 'Multi-Inheritance Review Page Edit: '
          : 'New Account Review Page Edit: ';

        this.loggerService.info(
          {
            eventId,
            message: message + `${editRoute.split('/').pop()}`,
          },
          LoggerCodes.GENERAL_SUCCESS,
        );
        this.globalStore.dispatch(
          SetTransactionReviewInvestmentObjectivesMode({
            isMultiReviewPageReachedAndSkipInvestmentRevision:
              editRoute.endsWith(this.accountTriageUrl) && editInfo.editEvidenceID === 'trustInfo',
          }),
        );
        if (this.isMultiInheritanceReviewEditing) {
          this.globalStore.dispatch(
            SetTransactionReviewPageEditMode({ isReviewPageReachedAndEditMode: true }),
          );
          editTransferId &&
            this.globalStore.dispatch(ActiveDraftChanged({ transactionId: editTransferId }));
        } else {
          this.globalStore.dispatch(
            SetTransactionOAReviewPageEditMode({ isOAReviewPageReachedAndEditMode: true }),
          );
        }
        editTransferId && this.globalStore.dispatch(SetTransactionId({ id: editTransferId }));
      }

      if (
        editRoute === this.personalInfoUrl &&
        (isNewAccountReviewEditing || this.isMultiInheritanceReviewEditing)
      ) {
        this.globalStore.dispatch(setIsEditingProfile({ isEditing: true }));
      }

      if (
        editRoute === this.accountTriageUrl &&
        this.containerUrl === this.multiInheritanceNewAccountReviewUrl
      ) {
        this.globalStore.dispatch(setIsEditingAccount({ isEditing: true }));
      }

      if (editRoute === this.employmentInfoUrl && this.isMultiInheritanceReviewEditing) {
        this.globalStore.dispatch(setIsEditingEmployment({ isEditing: true }));
      }

      this.globalStore.dispatch(fromRootStore.Go({ path: [editRoute] }));
    }
  }

  userRemove(transactionId: string) {
    if (this.isMultiInheritanceReviewEditing) {
      this.globalStore
        .select(selectTransactionById(transactionId))
        .pipe(take(1))
        .subscribe((transaction) => {
          if (transaction?.draftId) {
            this.globalStore.dispatch(
              DeleteDrafts({ ids: [transaction.draftId], skipDocumentCleanup: true }),
            );
          }
        });
    }
    this.globalStore.dispatch(DeleteTransactionState({ id: transactionId }));
    this.globalStore.dispatch(CheckFirstNewAccountTransaction());
  }

  handleNoRoutingEditing(event: IReceipt | IEditInfo) {
    this.noRoutingEditing.emit(event);
  }
}
